import { Link } from 'react-router-dom';
import { InfoRow, StatusBadge } from '../servers/ServerWidgets';
import { humanizeText, projectLabel } from './securityLabels';

export const cardStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '1.1rem 1.25rem',
  border: '1.5px solid #f1f5f9',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export function formatAgo(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const diff = Date.now() - date.getTime();
  if (diff < 60000) return 'just now';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

export function BackLink() {
  return (
    <Link to="/cybersecurity" className="sec-back">
      ← All projects
    </Link>
  );
}

export function backendLabel(source) {
  return projectLabel(source);
}

export function eventSummary(row) {
  const payload = row.payload || {};
  const who = payload.user_email || payload.login_hint || (row.user_id ? `user ${row.user_id}` : 'Guest');
  const req = row.action || (payload.method && payload.path ? `${payload.method} ${payload.path}` : 'request');
  const status = payload.http_status;
  if (row.type === 'vin_pull') return `${who} pulled ${row.count || 0} vehicle IDs in under a minute`;
  if (row.type === 'burst_action') return `${who} repeated ${req} ${row.count || 0} times in under a minute`;
  if (row.type === 'form_entry_human') {
    return `${who} filled ${payload.cars || row.count || 0} cars in ${Math.round((payload.age_ms || 0) / 60000)} min (${payload.sec_per_car || 0}s/car)`;
  }
  if (row.type === 'form_entry_watch') {
    return `${who} submitted ${payload.cars || row.count || 0} cars faster than usual (${payload.sec_per_car || 0}s/car)`;
  }
  if (row.type === 'form_entry_automation') {
    return `${who} submitted ${payload.cars || row.count || 0} cars with no human fill time (${payload.sec_per_car || 0}s/car)`;
  }
  if (row.type === 'user_monitored') return `${who} moved to the watch list`;
  if (row.type === 'ip_block_threshold') return `${row.ip || who} reached ${row.count || 0} blocks`;
  if (row.type === 'registry_vehicle_updates_threshold') {
    return `${row.count || 0} non-officer vehicles updated today (threshold ${payload.threshold || 500})`;
  }
  if (row.type === 'registry_update_burst') {
    return `${row.count || 0} vehicle updates in under a minute (limit ${payload.threshold || 50})`;
  }
  if (row.type === 'vin_harvest_hourly') return `${who} pulled ${row.count || 0} distinct vehicle IDs in an hour`;
  if (row.type === 'vin_harvest_daily') return `${who} pulled ${row.count || 0} distinct vehicle IDs today`;
  if (row.type === 'vin_404_hunt') return `${who} looked up ${row.count || 0} vehicle IDs that do not exist`;
  if (row.type === 'inbound_new_ip') return `Partner key used from new IP ${row.ip || who}`;
  if (row.type === 'inbound_geo_blocked') return `${row.ip || who} blocked (country ${payload.country || 'unknown'})`;
  if (row.type === 'external_bypass_main') return `TTP key used from non-island IP ${row.ip || who}`;
  if (row.type === 'officer_delete_burst') return `${row.count || 0} blocked island delete attempts in under 15 minutes`;
  if (row.type === 'delete_blocked') return `Island delete blocked${payload.vin ? ` for ${payload.vin}` : ''}`;
  if (row.type === 'registry_identity_change') {
    const names = (payload.changes || []).map((field) => fieldPathLabel(field.path)).filter(Boolean);
    const what = names.length ? names.join(', ') : 'Make, year, or owner';
    return `${what} changed on ${payload.vin || 'a vehicle'}`;
  }
  if (row.type === 'registry_identity_burst') return `Identity fields changed on ${row.count || 0} cars in an hour`;
  if (row.type === 'ip_block') {
    const why = payload.source;
    if (why === 'ai_bot') return `${row.ip || who} blocked as an AI scraper`;
    if (why === 'rate_limit' || why === 'inbound_rate_limit') return `${row.ip || who} sent too many requests`;
    return `${row.ip || who} was blocked`;
  }
  if (status) return `${who} got ${status} on ${req}`;
  return `${who} · ${req}`;
}

export function VerdictBanner({ level, text }) {
  const tone = level === 'critical'
    ? { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' }
    : level === 'warning'
      ? { bg: '#fffbeb', color: '#92400e', border: '#fde68a' }
      : { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0' };
  return (
    <div style={{
      ...cardStyle,
      background: tone.bg,
      borderColor: tone.border,
      color: tone.color,
      fontWeight: 700,
      marginBottom: 16,
    }}>
      {humanizeText(text)}
    </div>
  );
}

export function IdentityCard({ user }) {
  if (!user) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: '#64748b' }}>No account — login/scan guest</p>
      </div>
    );
  }
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', fontWeight: 700, letterSpacing: '0.04em' }}>WHO</p>
          <h2 style={{ margin: '6px 0 0', fontSize: 20 }}>{user.label || user.email || 'Unknown'}</h2>
        </div>
        <StatusBadge status={user.guest ? 'pending' : 'online'} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginTop: 14 }}>
        <InfoRow label="Email" value={user.email} />
        <InfoRow label="Role" value={user.role} />
        <InfoRow label="User ID" value={user.id} />
        <InfoRow label="Account" value={user.guest ? 'Guest / no token user' : (user.is_active === false ? 'Inactive' : 'Active')} />
      </div>
    </div>
  );
}

export function HitsTable({ hits, source }) {
  if (!hits || hits.length === 0) {
    return <p style={{ color: '#64748b', margin: 0 }}>No request hits recorded yet.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#94a3b8' }}>
            <th style={{ padding: '8px 6px' }}>When</th>
            <th style={{ padding: '8px 6px' }}>Request</th>
            <th style={{ padding: '8px 6px' }}>Status</th>
            <th style={{ padding: '8px 6px' }}>Who</th>
          </tr>
        </thead>
        <tbody>
          {hits.map((hit) => (
            <tr key={`${source || 'hit'}-${hit.id}`} style={{ borderTop: '1px solid #f1f5f9' }}>
              <td style={{ padding: '8px 6px', color: '#64748b', whiteSpace: 'nowrap' }}>{formatWhen(hit.created_at)}</td>
              <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                {hit.method || '—'} {hit.path || '—'}
              </td>
              <td style={{ padding: '8px 6px', color: hit.http_status >= 400 ? '#b91c1c' : '#0f766e', fontWeight: 700 }}>
                {hit.http_status || '—'}
              </td>
              <td style={{ padding: '8px 6px' }}>
                {hit.user_email || hit.login_hint || (hit.user_id ? `user ${hit.user_id}` : 'Guest')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <div style={{ ...cardStyle, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{title}</h3>
      {children}
    </div>
  );
}

const FIELD_PATH_LABELS = {
  'vehicle_details.make': 'Make',
  'vehicle_details.year': 'Year',
  'vehicle_details.ownership': 'Owner',
  'vehicle_details.ownership.owner': 'Owner',
  'vehicle_details.ownership.name': 'Owner name',
};

export function fieldPathLabel(path) {
  if (!path) return '—';
  return FIELD_PATH_LABELS[path] || String(path).split('.').pop();
}

export function valueText(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    if (value.name || value.owner) return String(value.name || value.owner);
    return JSON.stringify(value);
  }
  return String(value);
}

export function FieldChanges({ changes, emptyText = 'No field diffs recorded.' }) {
  const rows = Array.isArray(changes) ? changes : [];
  if (!rows.length) {
    return <p style={{ margin: 0, color: '#64748b' }}>{emptyText}</p>;
  }
  return (
    <div>
      {rows.map((field, index) => (
        <div key={`${field.path || 'field'}-${index}`} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
          <strong style={{ fontSize: 13 }}>{fieldPathLabel(field.path)}</strong>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8', textDecoration: 'line-through' }}>{valueText(field.old)}</p>
          <p style={{ margin: '2px 0 0', fontSize: 14, color: '#065f46', fontWeight: 700 }}>{valueText(field.new)}</p>
        </div>
      ))}
    </div>
  );
}
